import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import Breadcrumbs from '../components/shared/Breadcrumbs';
import AffiliateBanner from '../components/shared/AffiliateBanner';
import CTABox from '../components/shared/CTABox';
import TableOfContents from '../components/shared/TableOfContents';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const LTDUKPage = () => {
  const tocItems = [
    { id: 'was-ist-ltd', title: 'Was ist eine LTD?', level: 2 },
    { id: 'vorteile', title: 'Vorteile einer britischen LTD', level: 2 },
    { id: 'ltd-vs-llc', title: 'LTD vs. LLC Vergleich', level: 2 },
    { id: 'voraussetzungen', title: 'Voraussetzungen', level: 2 },
    { id: 'schritt-fuer-schritt', title: 'Schritt-für-Schritt Anleitung', level: 2 },
    { id: 'kosten', title: 'Kosten im Überblick', level: 2 },
    { id: 'steuern', title: 'Steuern und Buchhaltung', level: 2 },
    { id: 'faq', title: 'Häufig gestellte Fragen', level: 2 },
  ];

  const faqs = [
    {
      question: 'Kann ich als Deutscher eine LTD in UK gründen?',
      answer: 'Ja, die Gründung einer LTD steht auch Nicht-UK-Bürgern offen. Sie benötigen eine Registered Office Address in England, Wales, Schottland oder Nordirland sowie mindestens einen Director. Als Ausländer können Sie beide Anforderungen über Dienstleister erfüllen.'
    },
    {
      question: 'Hat der Brexit Auswirkungen auf die LTD-Gründung?',
      answer: 'Der Brexit hat die Gründung selbst nicht erschwert. Allerdings gelten für EU-Bürger nun andere Regeln, wenn sie im UK arbeiten oder leben möchten. Für die reine Unternehmenseigentümerschaft gibt es keine Einschränkungen.'
    },
    {
      question: 'Wie lange dauert die Gründung einer LTD?',
      answer: 'Bei Online-Gründung über Companies House kann die LTD innerhalb von 24 Stunden registriert sein. Der Standard-Service dauert 2-3 Werktage. Über Gründungsdienstleister ist oft eine beschleunigte Gründung am selben Tag möglich.'
    },
    {
      question: 'Brauche ich einen UK-Anwalt für die Gründung?',
      answer: 'Nein, für eine Standard-Gründung ist kein Anwalt erforderlich. Der Prozess ist bewusst einfach gehalten und kann online durchgeführt werden. Bei komplexeren Strukturen oder spezifischen rechtlichen Fragen kann eine Beratung sinnvoll sein.'
    },
    {
      question: 'Was ist der Unterschied zwischen England und Schottland bei der LTD?',
      answer: 'LTDs können in England & Wales, Schottland oder Nordirland registriert werden. Die Grundregeln sind ähnlich, aber es gibt kleinere Unterschiede im Gesellschaftsrecht. Für die meisten internationalen Gründer ist eine englische LTD die Standardwahl.'
    },
    {
      question: 'Muss ich in UK steuerpflichtig sein?',
      answer: 'Eine in UK registrierte LTD unterliegt der britischen Corporation Tax. Als Nicht-UK-Resident können zusätzliche Regeln in Ihrem Heimatland gelten. Wir empfehlen die Beratung durch einen auf internationales Steuerrecht spezialisierten Steuerberater.'
    },
    {
      question: 'Kann ich ein UK-Bankkonto eröffnen?',
      answer: 'Ja, aber es ist für Nicht-UK-Residenten schwieriger geworden. Digitale Banken wie Wise Business, Revolut Business oder Starling sind oft die bessere Option für internationale LTD-Inhaber.'
    },
    {
      question: 'Was ist ein PSC (Person with Significant Control)?',
      answer: 'Seit 2016 müssen LTDs Personen mit signifikanter Kontrolle (PSC) offenlegen. Das betrifft jeden, der mehr als 25% der Anteile oder Stimmrechte hält. Diese Informationen sind öffentlich einsehbar bei Companies House.'
    },
  ];

  return (
    <div className="py-12" data-testid="ltd-uk-page">
      <div className="container-wide">
        <Breadcrumbs items={[{ label: 'LTD Gründung UK', href: '/ltd-uk-gruenden' }]} />
        
        <AffiliateBanner />

        <div className="grid lg:grid-cols-12 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-8">
            <article className="prose-reviewlex">
              {/* Hero */}
              <header className="mb-12">
                <h1 className="mb-6">LTD Gründung UK — Der komplette Leitfaden 2026</h1>
                <p className="text-xl text-slate-600 mb-6">
                  Alles über die Gründung einer britischen Private Limited Company (LTD): 
                  Voraussetzungen, Kosten, Steuern und der Schritt-für-Schritt Gründungsprozess.
                </p>
                <div className="flex items-center gap-4 text-sm text-slate-500">
                  <span>Aktualisiert: April 2026</span>
                  <span>•</span>
                  <span>Lesezeit: 12 Min.</span>
                </div>
              </header>

              {/* Was ist eine LTD */}
              <section id="was-ist-ltd" className="mb-16">
                <h2>Was ist eine LTD (Private Limited Company)?</h2>
                <p>
                  Die <strong>Private Limited Company (LTD)</strong> ist die häufigste Unternehmensform im 
                  Vereinigten Königreich. Sie ist vergleichbar mit der deutschen GmbH — eine 
                  Kapitalgesellschaft mit beschränkter Haftung, deren Anteile nicht öffentlich gehandelt werden.
                </p>
                <p>
                  Die LTD wird durch den UK Companies Act 2006 geregelt und bei Companies House, dem 
                  britischen Handelsregister, eingetragen. Sie bietet Haftungsschutz für die Gesellschafter 
                  (Shareholders) und ist international als seriöse Rechtsform anerkannt.
                </p>
                <p>
                  Für deutsche Unternehmer ist die LTD besonders interessant durch die niedrigen 
                  Gründungskosten (ab £12), den schnellen Gründungsprozess und den starken internationalen 
                  Ruf des britischen Rechtssystems.
                </p>
              </section>

              {/* Vorteile */}
              <section id="vorteile" className="mb-16">
                <h2>Vorteile einer britischen LTD</h2>
                
                <div className="grid gap-6 mb-8">
                  <div className="card-flat p-6">
                    <h4 className="font-heading font-bold text-navy-900 mb-2 flex items-center gap-2">
                      <Check className="w-5 h-5 text-teal-600" />
                      Extrem niedrige Gründungskosten
                    </h4>
                    <p className="text-slate-600 text-sm mb-0">
                      Die Gründung bei Companies House kostet nur £12 online. Das ist deutlich günstiger 
                      als eine GmbH in Deutschland (Notar, Stammkapital).
                    </p>
                  </div>

                  <div className="card-flat p-6">
                    <h4 className="font-heading font-bold text-navy-900 mb-2 flex items-center gap-2">
                      <Check className="w-5 h-5 text-teal-600" />
                      Schnelle Gründung
                    </h4>
                    <p className="text-slate-600 text-sm mb-0">
                      Eine LTD kann innerhalb von 24 Stunden gegründet sein. Der gesamte Prozess 
                      erfolgt digital — kein Notartermin, keine Wartezeiten.
                    </p>
                  </div>

                  <div className="card-flat p-6">
                    <h4 className="font-heading font-bold text-navy-900 mb-2 flex items-center gap-2">
                      <Check className="w-5 h-5 text-teal-600" />
                      Kein Mindestkapital
                    </h4>
                    <p className="text-slate-600 text-sm mb-0">
                      Anders als bei der GmbH gibt es kein vorgeschriebenes Mindestkapital. 
                      Eine LTD kann theoretisch mit £1 Stammkapital gegründet werden.
                    </p>
                  </div>

                  <div className="card-flat p-6">
                    <h4 className="font-heading font-bold text-navy-900 mb-2 flex items-center gap-2">
                      <Check className="w-5 h-5 text-teal-600" />
                      Starker internationaler Ruf
                    </h4>
                    <p className="text-slate-600 text-sm mb-0">
                      Das britische Rechtssystem genießt weltweit hohes Ansehen. Eine LTD wird 
                      international als seriöse Rechtsform anerkannt.
                    </p>
                  </div>

                  <div className="card-flat p-6">
                    <h4 className="font-heading font-bold text-navy-900 mb-2 flex items-center gap-2">
                      <Check className="w-5 h-5 text-teal-600" />
                      Wettbewerbsfähige Körperschaftsteuer
                    </h4>
                    <p className="text-slate-600 text-sm mb-0">
                      Der Körperschaftsteuersatz liegt bei 25% (bzw. 19% für kleine Unternehmen 
                      mit Gewinnen unter £50.000). Das ist konkurrenzfähig im europäischen Vergleich.
                    </p>
                  </div>
                </div>
              </section>

              {/* LTD vs LLC */}
              <section id="ltd-vs-llc" className="mb-16">
                <h2>LTD vs. LLC — Vergleich für internationale Gründer</h2>
                
                <div className="overflow-x-auto mb-8">
                  <Table>
                    <TableHeader>
                      <TableRow className="table-header">
                        <TableHead>Merkmal</TableHead>
                        <TableHead>UK LTD</TableHead>
                        <TableHead>US LLC</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell className="font-medium">Gründungskosten</TableCell>
                        <TableCell>Ab £12</TableCell>
                        <TableCell>Ab $39 + Staatsgebühren</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Besteuerung</TableCell>
                        <TableCell>Corporation Tax (19-25%)</TableCell>
                        <TableCell>Pass-Through (oder wählbar)</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Transparenz</TableCell>
                        <TableCell>Hoch (Companies House öffentlich)</TableCell>
                        <TableCell>Je nach Staat (WY/NM = privat)</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Buchhaltungspflicht</TableCell>
                        <TableCell>Jahresabschluss + Confirmation Statement</TableCell>
                        <TableCell>Je nach Staat minimal</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Ideal für</TableCell>
                        <TableCell>EU/UK-Geschäft, E-Commerce, Beratung</TableCell>
                        <TableCell>US-Markt, SaaS, digitale Produkte</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>

                <div className="card-highlight p-6">
                  <p className="mb-0 text-sm">
                    <strong>Unsere Empfehlung:</strong> Wenn Ihr Hauptmarkt die USA ist, ist eine LLC 
                    oft die bessere Wahl. Für europäisches Geschäft oder wenn Sie Wert auf niedrigste 
                    Gründungskosten legen, kann die LTD die richtige Entscheidung sein.
                  </p>
                </div>
              </section>

              {/* Voraussetzungen */}
              <section id="voraussetzungen" className="mb-16">
                <h2>Voraussetzungen für die Gründung</h2>
                <p>
                  Die Anforderungen für eine LTD-Gründung sind bewusst niedrig gehalten:
                </p>

                <div className="space-y-4 my-8">
                  <div className="card-flat p-6">
                    <h4 className="font-heading font-bold text-navy-900 mb-2">Registered Office Address</h4>
                    <p className="text-slate-600 text-sm mb-0">
                      Eine physische Adresse in England, Wales, Schottland oder Nordirland. 
                      Für internationale Gründer bieten Dienstleister Registered Office Services an.
                    </p>
                  </div>

                  <div className="card-flat p-6">
                    <h4 className="font-heading font-bold text-navy-900 mb-2">Mindestens ein Director</h4>
                    <p className="text-slate-600 text-sm mb-0">
                      Die LTD benötigt mindestens einen Director (Geschäftsführer). Dieser muss 
                      mindestens 16 Jahre alt sein. Keine UK-Staatsbürgerschaft erforderlich.
                    </p>
                  </div>

                  <div className="card-flat p-6">
                    <h4 className="font-heading font-bold text-navy-900 mb-2">Mindestens ein Shareholder</h4>
                    <p className="text-slate-600 text-sm mb-0">
                      Die LTD braucht mindestens einen Gesellschafter. Director und Shareholder 
                      können dieselbe Person sein — eine Ein-Personen-LTD ist möglich.
                    </p>
                  </div>

                  <div className="card-flat p-6">
                    <h4 className="font-heading font-bold text-navy-900 mb-2">Articles of Association</h4>
                    <p className="text-slate-600 text-sm mb-0">
                      Die Satzung der LTD. Es gibt Standard-Vorlagen (Model Articles), die für 
                      die meisten Gründungen ausreichend sind.
                    </p>
                  </div>

                  <div className="card-flat p-6">
                    <h4 className="font-heading font-bold text-navy-900 mb-2">SIC Code</h4>
                    <p className="text-slate-600 text-sm mb-0">
                      Ein Standard Industrial Classification Code, der Ihre Geschäftstätigkeit 
                      beschreibt. Wird bei der Registrierung ausgewählt.
                    </p>
                  </div>
                </div>
              </section>

              {/* Schritt für Schritt */}
              <section id="schritt-fuer-schritt" className="mb-16">
                <h2>Schritt-für-Schritt: LTD gründen</h2>

                <div className="space-y-6">
                  <div className="card-flat p-6">
                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-teal-600 text-white font-bold rounded-sm">
                        1
                      </div>
                      <div>
                        <h4 className="font-heading font-bold text-navy-900 mb-2">
                          Firmenname prüfen
                        </h4>
                        <p className="text-slate-600 text-sm mb-0">
                          Prüfen Sie auf der Companies House Website, ob Ihr gewünschter Name verfügbar ist. 
                          Der Name muss "Limited" oder "Ltd" enthalten und darf nicht mit bestehenden 
                          Unternehmen verwechselbar sein.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="card-flat p-6">
                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-teal-600 text-white font-bold rounded-sm">
                        2
                      </div>
                      <div>
                        <h4 className="font-heading font-bold text-navy-900 mb-2">
                          SIC Code wählen
                        </h4>
                        <p className="text-slate-600 text-sm mb-0">
                          Wählen Sie den passenden Branchencode für Ihre Geschäftstätigkeit. 
                          Sie können bis zu vier SIC Codes angeben.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="card-flat p-6">
                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-teal-600 text-white font-bold rounded-sm">
                        3
                      </div>
                      <div>
                        <h4 className="font-heading font-bold text-navy-900 mb-2">
                          Directors und Shareholders benennen
                        </h4>
                        <p className="text-slate-600 text-sm mb-0">
                          Geben Sie die persönlichen Daten der Directors und Shareholders an. 
                          Beachten Sie: Diese Informationen werden bei Companies House öffentlich.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="card-flat p-6">
                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-teal-600 text-white font-bold rounded-sm">
                        4
                      </div>
                      <div>
                        <h4 className="font-heading font-bold text-navy-900 mb-2">
                          Articles of Association
                        </h4>
                        <p className="text-slate-600 text-sm mb-0">
                          Verwenden Sie die Standard Model Articles oder erstellen Sie individuelle. 
                          Für die meisten Gründungen sind die Model Articles ausreichend.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="card-flat p-6">
                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-teal-600 text-white font-bold rounded-sm">
                        5
                      </div>
                      <div>
                        <h4 className="font-heading font-bold text-navy-900 mb-2">
                          Bei Companies House registrieren
                        </h4>
                        <p className="text-slate-600 text-sm mb-0">
                          Die Registrierung erfolgt online über den Companies House Web Filing Service 
                          (£12) oder über einen Gründungsdienstleister.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="card-flat p-6">
                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-teal-600 text-white font-bold rounded-sm">
                        6
                      </div>
                      <div>
                        <h4 className="font-heading font-bold text-navy-900 mb-2">
                          Bei HMRC für Corporation Tax registrieren
                        </h4>
                        <p className="text-slate-600 text-sm mb-0">
                          Innerhalb von 3 Monaten nach Geschäftsaufnahme müssen Sie Ihre LTD bei 
                          HMRC (Steuerbehörde) für die Corporation Tax registrieren.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="card-flat p-6">
                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-teal-600 text-white font-bold rounded-sm">
                        7
                      </div>
                      <div>
                        <h4 className="font-heading font-bold text-navy-900 mb-2">
                          Geschäftskonto eröffnen
                        </h4>
                        <p className="text-slate-600 text-sm mb-0">
                          Eröffnen Sie ein UK-Geschäftskonto. Für internationale Directors sind 
                          digitale Banken wie Wise Business oder Revolut oft die einfachste Option.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Kosten */}
              <section id="kosten" className="mb-16">
                <h2>Kosten im Überblick</h2>

                <div className="overflow-x-auto mb-8">
                  <Table>
                    <TableHeader>
                      <TableRow className="table-header">
                        <TableHead>Posten</TableHead>
                        <TableHead>Kosten</TableHead>
                        <TableHead>Häufigkeit</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell className="font-medium">Gründung (Companies House)</TableCell>
                        <TableCell>£12 (online)</TableCell>
                        <TableCell>Einmalig</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Registered Office Service</TableCell>
                        <TableCell>£50-150/Jahr</TableCell>
                        <TableCell>Jährlich</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Confirmation Statement</TableCell>
                        <TableCell>£13</TableCell>
                        <TableCell>Jährlich</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Buchhaltung/Jahresabschluss</TableCell>
                        <TableCell>£200-1000/Jahr</TableCell>
                        <TableCell>Jährlich (je nach Komplexität)</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </section>

              {/* Steuern */}
              <section id="steuern" className="mb-16">
                <h2>Steuern und Buchhaltung</h2>

                <h3 className="mt-8 mb-4">Corporation Tax</h3>
                <p>
                  Die Körperschaftsteuer beträgt 25% auf Gewinne (19% für kleine Unternehmen mit 
                  Gewinnen unter £50.000). Die Steuererklärung muss innerhalb von 12 Monaten nach 
                  Ende des Geschäftsjahres eingereicht werden.
                </p>

                <h3 className="mt-8 mb-4">VAT (Umsatzsteuer)</h3>
                <p>
                  Die Registrierung für VAT ist Pflicht, wenn Ihr steuerpflichtiger Umsatz £90.000 
                  pro Jahr übersteigt. Der Standard-Steuersatz beträgt 20%.
                </p>

                <h3 className="mt-8 mb-4">Making Tax Digital (MTD)</h3>
                <p>
                  Das britische MTD-Programm erfordert die digitale Buchführung und elektronische 
                  Übermittlung von Steuerdaten. Viele Buchhaltungssoftware-Lösungen sind MTD-konform.
                </p>

                <h3 className="mt-8 mb-4">Jährliche Pflichten</h3>
                <ul>
                  <li><strong>Confirmation Statement:</strong> Jährliche Bestätigung der Unternehmensdaten bei Companies House</li>
                  <li><strong>Annual Accounts:</strong> Jahresabschluss bei Companies House einreichen</li>
                  <li><strong>Corporation Tax Return:</strong> Steuererklärung bei HMRC</li>
                </ul>
              </section>

              {/* FAQ */}
              <section id="faq" className="mb-16">
                <h2>Häufig gestellte Fragen</h2>
                
                <Accordion type="single" collapsible className="w-full">
                  {faqs.map((faq, index) => (
                    <AccordionItem key={index} value={`faq-${index}`}>
                      <AccordionTrigger className="text-left font-heading font-semibold text-navy-900">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-slate-600">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </section>

            </article>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-4">
            <div className="space-y-8">
              <TableOfContents items={tocItems} />

              <CTABox
                title="US-LLC statt UK-LTD?"
                provider="Northwest Registered Agent"
                rating={4.9}
                variant="sidebar"
                highlights={[
                  'Ab $39 + Staatsgebühren',
                  'Mehr Datenschutz als UK-LTD',
                  'Ideal für US-Markt'
                ]}
              />

              <div className="bg-slate-50 border border-slate-200 rounded-sm p-6">
                <h4 className="font-heading font-bold text-navy-900 mb-4">Verwandte Ratgeber</h4>
                <ul className="space-y-3">
                  <li>
                    <Link to="/llc-usa-gruenden" className="text-sm text-slate-700 hover:text-teal-600">
                      → LLC Gründung USA
                    </Link>
                  </li>
                  <li>
                    <Link to="/ratgeber/ltd-england-vs-schottland" className="text-sm text-slate-700 hover:text-teal-600">
                      → England vs. Schottland LTD
                    </Link>
                  </li>
                  <li>
                    <Link to="/ratgeber/firmenkonto-uk-eroeffnen" className="text-sm text-slate-700 hover:text-teal-600">
                      → UK-Firmenkonto eröffnen
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default LTDUKPage;
